// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/deprecate-formik
import { FormikProps } from 'formik'
import { FC, HTMLAttributes } from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { RequiredAsterisk } from '../../../../atoms/RequiredAsterisk'
import { EmbroideryTextInput } from '../../EmbroideryTextInput'
import { EmbroideryInputs, EmbroideryOptionWrapper, Label } from '../../shared-form-styles'

export type NameInputProps<F extends FormikProps<V>, V> = {
  // Rely on intersection to support constraints as inference site
  // See: https://github.com/microsoft/TypeScript/issues/7234#issuecomment-223627422
  form: F & FormikProps<V>
  maxCharacterCount: number
  ariaLabelledby?: string
  placeholder?: string
  name: keyof V
}
/**
 * An individual embroidery name input field.
 *
 * @param props
 * @param props.form - The current Formik form object. Used for reading the current value, error, and touched state for the given input name.
 * @param maxCharacterCount - Define a maximum character count for the text input.
 * @param placeholder - The placeholder text that is displayed for the input.
 * @param name - The name attribute for the input. This should coorelate to the tracked value in the passed `form` object.
 * @param ariaLabelledBy - The `aria-labelledby` attribute that should be applied to the input. This should match the `label` prop that was passed to `Name.Wrapper`.
 */
export const Input = <F extends FormikProps<V>, V>({
  form,
  maxCharacterCount,
  placeholder,
  name,
  ariaLabelledby,
}: NameInputProps<F, V>) => {
  return (
    <EmbroideryTextInput
      ariaLabelledby={ariaLabelledby}
      errorMessage={form.touched[name] && form.errors[name] ? String(form.errors[name]) : undefined}
      maxCharacterCount={maxCharacterCount}
      name={String(name)}
      onChange={form.handleChange}
      placeholder={placeholder ?? ''}
      value={String(form.values[name] ?? '')}
    />
  )
}

/**
 * A wrapper for the embroidery name form.
 *
 * Children of this component are expected to be an instance of `<Name.Input />`.
 *
 * @param props
 * @param props.label - The `label` attribute to apply to the label text element. Should match the `ariaLabelledby` prop that is provided to `Name.Input`.
 * @param props.title - The title to display for the name section. Defaults to our predefined fixture for this title value.
 */
export type NameWrapperProps = {
  label: string
  title?: string
  required?: boolean
} & HTMLAttributes<HTMLDivElement>
export const Wrapper: FC<NameWrapperProps> = ({
  label,
  children,
  title,
  required = false,
  ...rest
}) => {
  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()
  return (
    <EmbroideryOptionWrapper {...rest}>
      <Label id={label}>
        <span>
          {title ?? fixtures.textOption.textLabel}
          {required && <RequiredAsterisk />}
        </span>
      </Label>
      <EmbroideryInputs>{children}</EmbroideryInputs>
    </EmbroideryOptionWrapper>
  )
}
